import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { styles } from "./stylesConversations";
import ConversationItem from "./components/ConversationItem";
import ThreadPanel from "./components/ThreadPanel";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useConversationsUnread } from "@/hooks/useConversationsUnread";
import { useGetConversationsQuery } from "@/store/api/conversationsApi";
import {
  freezeUnreadCount,
  markConversationsRead,
  resetFrozenUnreadCount,
} from "@/store/slice/conversationsUnreadSlice";
import { ConversationTab, IConversation } from "@/store/api/conversationsApiData";

const UNREAD_HIGHLIGHT_MS = 2500;

const pickUnreadUids = (items: IConversation[], unreadCount: number | null) => {
  const flagged = items.filter((item) => item.isRead === false);
  if (flagged.length) return new Set(flagged.map((item) => item.uid));
  if (!unreadCount || unreadCount <= 0) return new Set<string>();
  return new Set(items.slice(0, unreadCount).map((item) => item.uid));
};

const groupLabel = (createdAt: string) => {
  const date = dayjs(createdAt);
  const today = dayjs().startOf("day");
  const diff = today.diff(date.startOf("day"), "day");
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  return date.format("MMMM D, YYYY");
};

const Conversations = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as ConversationTab) || "all";

  const [selected, setSelected] = useState<IConversation | null>(null);

  const { frozenCount, serverCount, isReady: isUnreadReady } = useConversationsUnread();

  useEffect(() => {
    if (isUnreadReady) dispatch(freezeUnreadCount(serverCount ?? 0));
  }, [isUnreadReady, serverCount, dispatch]);

  const { data, isLoading, isFetching, isSuccess } = useGetConversationsQuery(
    { tab: activeTab },
    { refetchOnMountOrArgChange: true, skip: !isUnreadReady }
  );

  const conversations: IConversation[] = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data]
  );

  const [highlightedUids, setHighlightedUids] = useState<Set<string>>(new Set());
  const highlightStartedRef = useRef(false);
  const listLoadedRef = useRef(false);

  useEffect(() => {
    if (highlightStartedRef.current) return;
    if (!isSuccess || isFetching) return;

    highlightStartedRef.current = true;
    listLoadedRef.current = true;

    const unreadUids = pickUnreadUids(conversations, frozenCount);
    if (!unreadUids.size) {
      dispatch(markConversationsRead());
      dispatch(resetFrozenUnreadCount());
      return;
    }

    setHighlightedUids(unreadUids);
  }, [isSuccess, isFetching, conversations, frozenCount, dispatch]);

  useEffect(() => {
    if (!highlightedUids.size) return;
    const timer = setTimeout(() => {
      setHighlightedUids(new Set());
      dispatch(markConversationsRead());
      dispatch(resetFrozenUnreadCount());
    }, UNREAD_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightedUids, dispatch]);

  useEffect(() => {
    return () => {
      if (listLoadedRef.current) dispatch(markConversationsRead());
      dispatch(resetFrozenUnreadCount());
    };
  }, [dispatch]);

  const grouped = useMemo(() => {
    const groups: { label: string; items: IConversation[] }[] = [];
    conversations.forEach((conversation) => {
      const label = groupLabel(conversation.createdAt);
      const existing = groups.find((group) => group.label === label);
      if (existing) existing.items.push(conversation);
      else groups.push({ label, items: [conversation] });
    });
    return groups;
  }, [conversations]);

  const goToPost = (postUid: string) => {
    if (postUid) navigate(`/post/${postUid}`);
  };

  return (
    <Box sx={styles.page}>
      <Box sx={styles.listColumn} className="hide-scrollbar">
        {!isUnreadReady || isLoading ? (
          <Box sx={styles.loaderBox}>
            <CustomLoader />
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={styles.emptyState}>
            <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>No conversations yet</Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Replies and mentions on your posts will show up here.
            </Typography>
          </Box>
        ) : (
          grouped.map((group) => (
            <Box key={group.label}>
              <Typography sx={styles.sectionLabel}>{group.label}</Typography>
              {group.items.map((conversation) => (
                <ConversationItem
                  key={conversation.uid}
                  conversation={conversation}
                  isSelected={selected?.uid === conversation.uid}
                  isUnread={highlightedUids.has(conversation.uid)}
                  onOpen={setSelected}
                  onReply={setSelected}
                  onGoToPost={(item) => goToPost(item.post.uid)}
                />
              ))}
            </Box>
          ))
        )}
      </Box>

      {selected && (
        <ThreadPanel
          conversationUid={selected.uid}
          onClose={() => setSelected(null)}
          onGoToPost={goToPost}
        />
      )}
    </Box>
  );
};

export default Conversations;
